import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Download, BarChart3, ShieldCheck, Users, Mail, HandCoins, Activity, CalendarClock, UserCog, ChevronDown, ChevronUp, MailPlus, MailX, X, ShieldAlert, Timer } from "lucide-react";
import { useUiState } from "@/lib/uiState";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const PERIODS = [
  { key: "30d", label: "Last 30 days" },
  { key: "month", label: "This month" },
  { key: "lastmonth", label: "Last month" },
  { key: "90d", label: "Last 90 days" },
  { key: "ytd", label: "This year" },
  { key: "all", label: "All time" },
];

const isoDate = (d) => d.toISOString().slice(0, 10);
function rangeFor(key) {
  const now = new Date();
  if (key === "all") return { start: "", end: "" };
  if (key === "30d") { const s = new Date(now); s.setDate(s.getDate() - 30); return { start: isoDate(s), end: "" }; }
  if (key === "90d") { const s = new Date(now); s.setDate(s.getDate() - 90); return { start: isoDate(s), end: "" }; }
  if (key === "month") return { start: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), end: "" };
  if (key === "lastmonth") return { start: isoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)), end: isoDate(new Date(now.getFullYear(), now.getMonth(), 0)) };
  if (key === "ytd") return { start: isoDate(new Date(now.getFullYear(), 0, 1)), end: "" };
  return { start: "", end: "" };
}
const monthLabel = (m) => { const [y, mo] = m.split("-"); return new Date(y, mo - 1, 1).toLocaleString(undefined, { month: "short", year: "numeric" }); };
const bucketLabel = (b) => {
  if (!b) return "—";
  if (b.includes("-W")) return b.replace("-W", " · W");
  const parts = b.split("-");
  if (parts.length === 3) return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (parts.length === 2) return monthLabel(b);
  return b;
};

export default function Reports() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [emailModal, setEmailModal] = useState(null); // {title, rows}
  const [stats, setStats] = useState({});
  const [fund, setFund] = useState({});
  const [audit, setAudit] = useState([]);
  const [period, setPeriod] = useUiState("ud_report_period", "30d");
  const [activity, setActivity] = useState(null);
  const [loadingAct, setLoadingAct] = useState(false);
  const [actOpen, setActOpen] = useUiState("ud_report_activity_open", true);
  const [classOpen, setClassOpen] = useUiState("ud_report_class_open", true);
  const [auditOpen, setAuditOpen] = useUiState("ud_report_audit_open", true);
  const [dsarOpen, setDsarOpen] = useUiState("ud_report_dsar_open", true);
  const [dsar, setDsar] = useState([]);
  const [retOpen, setRetOpen] = useUiState("ud_report_ret_open", true);
  const [retYears, setRetYears] = useState(7);
  const [retData, setRetData] = useState(null);
  const [retBusy, setRetBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useUiState("ud_report_email_open", true);
  const [emailPeriod, setEmailPeriod] = useUiState("ud_report_email_period", "ytd");
  const [emailGroup, setEmailGroup] = useUiState("ud_report_email_group", "month");
  const [emailData, setEmailData] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/fundraising/stats").then((r) => setFund(r.data)).catch(() => {});
    if (isAdmin) api.get("/auth/login-audit?limit=50").then((r) => setAudit(r.data.audit || r.data || [])).catch(() => {});
    if (isAdmin) api.get("/admin/dsar-audit?limit=200").then((r) => setDsar(r.data.entries || [])).catch(() => {});
    if (isAdmin) api.get("/admin/retention").then((r) => { setRetYears(r.data.years || 7); }).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    const { start, end } = rangeFor(period);
    setLoadingAct(true);
    api.get("/reports/alumni-activity", { params: { start, end } })
      .then((r) => setActivity(r.data)).catch(() => setActivity(null)).finally(() => setLoadingAct(false));
  }, [period]);

  useEffect(() => {
    const { start, end } = rangeFor(emailPeriod);
    setLoadingEmail(true);
    api.get("/reports/email-activity", { params: { start, end, group: emailGroup } })
      .then((r) => setEmailData(r.data)).catch(() => setEmailData(null)).finally(() => setLoadingEmail(false));
  }, [emailPeriod, emailGroup]);

  const exportCsv = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (e) { toast.error("Export failed"); }
  };

  const exportActivityCsv = () => {
    const rows = (activity?.recent || []).map((r) => [
      r.at ? new Date(r.at).toLocaleString() : "", r.by_name || r.by, r.action, r.detail || "",
      r.alumnus, r.class_year || "",
    ]);
    const header = ["When", "Updated by", "Action", "Detail", "Alumnus", "Class Year"];
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `alumni-activity-${period}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Activity report downloaded");
  };

  const exportDsarCsv = () => {
    const rows = (dsar || []).map((r) => [
      r.at ? new Date(r.at).toLocaleString() : "", r.action === "delete" ? "erasure" : "export",
      r.subject_name || "", r.subject_id || "", r.by_name || r.by || "",
    ]);
    const header = ["When", "Action", "Subject", "Subject ID", "Performed by"];
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `dsar-audit-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("DSAR audit log downloaded");
  };

  const runRetentionReview = async () => {
    setRetBusy(true);
    try {
      await api.put("/admin/retention", { years: Number(retYears) });
      const r = await api.get(`/admin/retention/review?years=${Number(retYears)}`);
      setRetData(r.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not run retention review");
    } finally { setRetBusy(false); }
  };

  const exportEmailCsv = () => {
    const rows = (emailData?.series || []).map((s) => [bucketLabel(s.period), s.period, s.new_emails, s.opt_outs, s.opt_ins]);
    const header = ["Period", "Bucket", "New Emails", "Opt-Outs", "Opt-Ins"];
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `email-activity-${emailPeriod}-${emailGroup}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Email report downloaded");
  };

  const tiles = [
    ["Total Alumni", stats.total || 0, Users], ["With Email", stats.with_email || 0, Mail],
    ["Total Raised", money(fund.total_raised), HandCoins], ["Donors", fund.donor_count || 0, Users],
  ];
  const monthMax = Math.max(...((activity?.by_month || []).map((x) => x.count)), 1);

  return (
    <div className="fade-up">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Administration &amp; Reporting</div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Reports &amp; Exports</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-alumni" onClick={() => exportCsv("/export/alumni.csv", "alumni.csv")} className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm hover:bg-muted"><Download className="h-4 w-4" /> Alumni CSV</button>
          <button data-testid="export-gifts" onClick={() => exportCsv("/export/gifts.csv", "gifts.csv")} className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm hover:bg-muted"><Download className="h-4 w-4" /> Gifts CSV</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {tiles.map(([l, v, Icon]) => (
          <div key={l} data-testid={`report-tile-${l.split(" ")[0].toLowerCase()}`} className="bg-card border border-border rounded-lg px-5 py-3.5 flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
            <div className="min-w-0">
              <div className="text-2xl font-semibold leading-tight">{v}</div>
              <div className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground truncate">{l}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Alumni Update Activity */}
      <div className="bg-card border border-border rounded-lg overflow-hidden mb-8" data-testid="activity-report">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <button type="button" data-testid="activity-collapse" onClick={() => setActOpen(!actOpen)} className="font-serif text-xl tracking-tight flex items-center gap-2 hover:text-primary transition-colors">
            {actOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            <Activity className="h-5 w-5 text-primary" /> Alumni Update Activity
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex flex-wrap gap-1">
              {PERIODS.map((p) => (
                <button key={p.key} data-testid={`activity-period-${p.key}`} onClick={() => setPeriod(p.key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${period === p.key ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-muted"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <button data-testid="activity-export-csv" onClick={exportActivityCsv} disabled={!activity?.recent?.length}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs hover:bg-muted disabled:opacity-40"><Download className="h-3.5 w-3.5" /> CSV</button>
          </div>
        </div>

        {actOpen && (<>
        <div className="px-5 py-4">
          <div className="flex items-baseline gap-2 mb-4">
            <span data-testid="activity-total" className="text-3xl font-semibold">{loadingAct ? "…" : (activity?.total ?? 0)}</span>
            <span className="text-sm text-muted-foreground">alumni updates in this period</span>
          </div>

          {(!activity || activity.total === 0) && !loadingAct && (
            <div className="text-sm text-muted-foreground py-4">No alumni updates recorded in this period.</div>
          )}

          {activity && activity.total > 0 && (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* By staff member */}
              <div>
                <h3 className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><UserCog className="h-4 w-4" /> By staff member</h3>
                <div className="space-y-2" data-testid="activity-by-user">
                  {activity.by_user.map((u) => {
                    const topActions = Object.entries(u.actions).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k, v]) => `${k} (${v})`).join(" · ");
                    return (
                      <div key={u.by} data-testid={`activity-user-${u.by}`} className="flex items-start justify-between gap-3 border-b border-border pb-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{u.by_name}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{topActions}</div>
                        </div>
                        <span className="shrink-0 text-sm font-semibold tabular-nums">{u.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* By month */}
              <div>
                <h3 className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> By month</h3>
                <div className="space-y-1.5" data-testid="activity-by-month">
                  {activity.by_month.slice().reverse().map((m) => (
                    <div key={m.month} className="flex items-center gap-3 text-sm">
                      <span className="w-20 text-muted-foreground font-mono text-xs">{monthLabel(m.month)}</span>
                      <div className="flex-1 h-4 rounded bg-muted overflow-hidden"><div className="h-full bg-accent" style={{ width: `${(m.count / monthMax) * 100}%` }} /></div>
                      <span className="w-8 text-right font-medium tabular-nums">{m.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Recent updates feed */}
        {activity && activity.recent && activity.recent.length > 0 && (
          <div className="border-t border-border">
            <div className="px-5 py-2.5 text-xs font-mono uppercase tracking-wide text-muted-foreground bg-muted/40">Recent updates</div>
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono sticky top-0"><tr>
                  <th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Updated by</th>
                  <th className="text-left px-4 py-2">Action</th><th className="text-left px-4 py-2">Alumnus</th>
                </tr></thead>
                <tbody className="divide-y divide-border" data-testid="activity-recent">
                  {activity.recent.slice(0, 100).map((r, i) => (
                    <tr key={i} className="hover:bg-muted/30">
                      <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                      <td className="px-4 py-2">{r.by_name || r.by}</td>
                      <td className="px-4 py-2"><span className="text-[11px]">{r.action}</span>{r.detail ? <span className="text-muted-foreground text-[11px]"> — {r.detail}</span> : null}</td>
                      <td className="px-4 py-2">{r.alumnus}{r.class_year ? <span className="text-muted-foreground text-xs"> ’{String(r.class_year).slice(-2)}</span> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>)}
      </div>

      {/* Email Growth & Opt-Outs */}
      <div className="bg-card border border-border rounded-lg overflow-hidden mb-8" data-testid="email-report">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <button type="button" data-testid="email-collapse" onClick={() => setEmailOpen(!emailOpen)} className="font-serif text-xl tracking-tight flex items-center gap-2 hover:text-primary transition-colors">
            {emailOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            <Mail className="h-5 w-5 text-primary" /> Email Growth &amp; Opt-Outs
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex flex-wrap gap-1">
              {PERIODS.map((p) => (
                <button key={p.key} data-testid={`email-period-${p.key}`} onClick={() => setEmailPeriod(p.key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${emailPeriod === p.key ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-muted"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex gap-1 border-l border-border pl-2">
              {[["day", "Day"], ["week", "Week"], ["month", "Month"]].map(([g, lbl]) => (
                <button key={g} data-testid={`email-group-${g}`} onClick={() => setEmailGroup(g)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${emailGroup === g ? "bg-accent text-accent-foreground" : "border border-border text-muted-foreground hover:bg-muted"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            <button data-testid="email-export-csv" onClick={exportEmailCsv} disabled={!emailData?.series?.length}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs hover:bg-muted disabled:opacity-40"><Download className="h-3.5 w-3.5" /> CSV</button>
          </div>
        </div>

        {emailOpen && (
          <div className="px-5 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
              <button type="button" data-testid="email-stat-new" onClick={() => setEmailModal({ title: "New emails captured", rows: emailData?.recent_new_emails || [], color: "emerald" })} className="flex items-center gap-3 text-left border border-border rounded-lg px-4 py-3 hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors">
                <MailPlus className="h-6 w-6 text-emerald-600 shrink-0" />
                <div className="min-w-0">
                  <div className="text-2xl font-semibold tabular-nums leading-none">{loadingEmail ? "…" : (emailData?.total_new_emails ?? 0)}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground mt-1 truncate">New emails captured</div>
                </div>
              </button>
              <button type="button" data-testid="email-stat-optout" onClick={() => setEmailModal({ title: "Opt-outs", rows: emailData?.recent_opt_outs || [], color: "rose" })} className="flex items-center gap-3 text-left border border-border rounded-lg px-4 py-3 hover:border-rose-400 hover:bg-rose-50/40 transition-colors">
                <MailX className="h-6 w-6 text-rose-600 shrink-0" />
                <div className="min-w-0">
                  <div className="text-2xl font-semibold tabular-nums leading-none">{loadingEmail ? "…" : (emailData?.total_opt_outs ?? 0)}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground mt-1 truncate">Opt-outs</div>
                  {emailData?.opt_outs_by_type && emailData.total_opt_outs > 0 && (
                    <div className="text-[10px] text-muted-foreground mt-0.5" data-testid="optout-breakdown">All {emailData.opt_outs_by_type.all} · Mktg {emailData.opt_outs_by_type.marketing} · Fin {emailData.opt_outs_by_type.financial}</div>
                  )}
                </div>
              </button>
              <button type="button" data-testid="email-stat-net" onClick={() => setEmailModal({ title: "Opt-ins / re-subscribes", rows: emailData?.recent_opt_ins || [], color: "primary" })} className="flex items-center gap-3 text-left border border-border rounded-lg px-4 py-3 hover:border-primary/50 hover:bg-primary/5 transition-colors">
                <Mail className="h-6 w-6 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="text-2xl font-semibold tabular-nums leading-none">{loadingEmail ? "…" : (emailData?.total_opt_ins ?? 0)}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground mt-1 truncate">Opt-ins / re-subscribes</div>
                </div>
              </button>
            </div>

            {(!emailData || emailData.series?.length === 0) && !loadingEmail && (
              <div className="text-sm text-muted-foreground py-4">No email activity recorded in this period.</div>
            )}

            {emailData && emailData.series?.length > 0 && (() => {
              const seriesMax = Math.max(...emailData.series.map((s) => Math.max(s.new_emails, s.opt_outs)), 1);
              return (
                <div>
                  <div className="flex items-center gap-4 text-[11px] text-muted-foreground mb-2">
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500 inline-block" /> New emails</span>
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-rose-500 inline-block" /> Opt-outs</span>
                  </div>
                  <div className="space-y-2" data-testid="email-series">
                    {emailData.series.slice().reverse().map((s) => (
                      <div key={s.period} className="flex items-center gap-3 text-sm">
                        <span className="w-24 shrink-0 text-muted-foreground font-mono text-xs">{bucketLabel(s.period)}</span>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-3 rounded bg-muted overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${(s.new_emails / seriesMax) * 100}%` }} /></div>
                            <span className="w-8 text-right text-xs tabular-nums text-emerald-700">{s.new_emails}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-3 rounded bg-muted overflow-hidden"><div className="h-full bg-rose-500" style={{ width: `${(s.opt_outs / seriesMax) * 100}%` }} /></div>
                            <span className="w-8 text-right text-xs tabular-nums text-rose-700">{s.opt_outs}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {emailData.total_opt_outs > 0 && (
                    <div className="mt-6 pt-4 border-t border-border" data-testid="optout-trend">
                      <div className="flex items-center gap-4 text-[11px] text-muted-foreground mb-2">
                        <span className="text-foreground font-medium">Opt-outs by type</span>
                        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-rose-500 inline-block" /> All email</span>
                        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-amber-500 inline-block" /> Marketing</span>
                        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-violet-500 inline-block" /> Financial</span>
                      </div>
                      {(() => {
                        const tmax = Math.max(...emailData.series.map((s) => (s.ob_all || 0) + (s.ob_mkt || 0) + (s.ob_fin || 0)), 1);
                        return (
                          <div className="space-y-1.5">
                            {emailData.series.slice().reverse().map((s) => {
                              const tot = (s.ob_all || 0) + (s.ob_mkt || 0) + (s.ob_fin || 0);
                              return (
                                <div key={s.period} className="flex items-center gap-3 text-sm">
                                  <span className="w-24 shrink-0 text-muted-foreground font-mono text-xs">{bucketLabel(s.period)}</span>
                                  <div className="flex-1 h-3 rounded bg-muted overflow-hidden flex" style={{ width: `${(tot / tmax) * 100}%`, minWidth: tot ? "2px" : 0 }}>
                                    <div className="h-full bg-rose-500" style={{ width: `${tot ? (s.ob_all / tot) * 100 : 0}%` }} title={`All: ${s.ob_all || 0}`} />
                                    <div className="h-full bg-amber-500" style={{ width: `${tot ? (s.ob_mkt / tot) * 100 : 0}%` }} title={`Marketing: ${s.ob_mkt || 0}`} />
                                    <div className="h-full bg-violet-500" style={{ width: `${tot ? (s.ob_fin / tot) * 100 : 0}%` }} title={`Financial: ${s.ob_fin || 0}`} />
                                  </div>
                                  <span className="w-8 text-right text-xs tabular-nums text-muted-foreground">{tot}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {emailData.opt_out_reasons && emailData.opt_out_reasons.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-border" data-testid="optout-reasons">
                      <div className="text-foreground font-medium text-[11px] mb-2">Top opt-out reasons</div>
                      <ul className="space-y-1">
                        {emailData.opt_out_reasons.map((r, i) => (
                          <li key={i} className="flex items-center justify-between gap-3 text-sm">
                            <span className="truncate text-muted-foreground">{r.reason}</span>
                            <span className="shrink-0 text-xs font-mono tabular-nums px-2 py-0.5 rounded-full bg-muted">{r.count}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg p-5 mb-8">
        <h2 className="font-serif text-xl tracking-tight flex items-center gap-2 mb-4">
          <button type="button" data-testid="class-collapse" onClick={() => setClassOpen(!classOpen)} className="flex items-center gap-2 hover:text-primary transition-colors">
            {classOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            <BarChart3 className="h-5 w-5 text-primary" /> Alumni by Class Year
          </button>
        </h2>
        {classOpen && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-4 text-[11px] text-muted-foreground mb-1">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-accent inline-block" /> Living</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-slate-400 inline-block" /> Deceased</span>
          </div>
          {(stats.by_class || []).slice(0, 12).map((c) => {
            const max = Math.max(...(stats.by_class || []).map((x) => x.count), 1);
            return (
              <div key={c.class_year} className="flex items-center gap-3 text-sm">
                <span className="w-14 text-muted-foreground font-mono text-xs">{c.class_year}</span>
                <div className="flex-1 h-4 rounded bg-muted overflow-hidden">
                  <div className="h-full flex" style={{ width: `${(c.count / max) * 100}%` }}>
                    <div className="h-full bg-accent" style={{ width: `${c.count ? (c.living / c.count) * 100 : 0}%` }} title={`${c.living || 0} living`} />
                    <div className="h-full bg-slate-400" style={{ width: `${c.count ? (c.deceased / c.count) * 100 : 0}%` }} title={`${c.deceased || 0} deceased`} />
                  </div>
                </div>
                <span className="w-8 text-right font-medium" title={`${c.living || 0} living · ${c.deceased || 0} deceased`}>{c.count}</span>
              </div>
            );
          })}
          {(!stats.by_class || stats.by_class.length === 0) && <div className="text-muted-foreground text-sm">No class data.</div>}
        </div>
        )}
      </div>

      {isAdmin && (
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="audit-panel">
          <button type="button" data-testid="audit-collapse" onClick={() => setAuditOpen(!auditOpen)} className="w-full px-5 py-3 border-b border-border flex items-center gap-2 hover:text-primary transition-colors">{auditOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}<ShieldCheck className="h-4 w-4 text-primary" /><span className="font-serif text-lg tracking-tight">Sign-in Activity</span></button>
          {auditOpen && audit.length === 0 && <div className="px-5 py-6 text-sm text-muted-foreground text-center">No sign-in activity recorded.</div>}
          {auditOpen && audit.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono"><tr><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">IP</th><th className="text-left px-4 py-2">Result</th></tr></thead>
              <tbody className="divide-y divide-border">
                {audit.map((r, i) => (
                  <tr key={i} className="hover:bg-muted/30">
                    <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2">{r.email}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.ip}</td>
                    <td className="px-4 py-2"><span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${r.success ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{r.success ? "success" : (r.reason || "failed")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="dsar-panel">
          <div className="w-full px-5 py-3 border-b border-border flex items-center justify-between gap-2">
            <button type="button" data-testid="dsar-collapse" onClick={() => setDsarOpen(!dsarOpen)} className="flex items-center gap-2 hover:text-primary transition-colors">{dsarOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}<ShieldAlert className="h-4 w-4 text-primary" /><span className="font-serif text-lg tracking-tight">Data Privacy Log (DSAR)</span></button>
            {dsar.length > 0 && (
              <button type="button" data-testid="dsar-export-csv" onClick={exportDsarCsv} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs font-medium hover:bg-muted"><Download className="h-3.5 w-3.5" /> Download CSV</button>
            )}
          </div>
          {dsarOpen && (dsar.length === 0
            ? <div className="px-5 py-6 text-sm text-muted-foreground text-center" data-testid="dsar-empty">No data exports or erasures have been performed yet.</div>
            : (
              <table className="w-full text-sm" data-testid="dsar-table">
                <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono"><tr><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Action</th><th className="text-left px-4 py-2">Subject</th><th className="text-left px-4 py-2">By</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {dsar.map((r, i) => (
                    <tr key={r.id || i} className="hover:bg-muted/30" data-testid="dsar-row">
                      <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                      <td className="px-4 py-2"><span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${r.action === "delete" ? "bg-red-100 text-red-800" : "bg-sky-100 text-sky-800"}`}>{r.action === "delete" ? "erasure" : "export"}</span></td>
                      <td className="px-4 py-2">{r.subject_name || r.subject_id}</td>
                      <td className="px-4 py-2 text-muted-foreground">{r.by_name || r.by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
        </div>
      )}

      {isAdmin && (
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="retention-panel">
          <button type="button" data-testid="retention-collapse" onClick={() => setRetOpen(!retOpen)} className="w-full px-5 py-3 border-b border-border flex items-center gap-2 hover:text-primary transition-colors">{retOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}<Timer className="h-4 w-4 text-primary" /><span className="font-serif text-lg tracking-tight">Data Retention Review</span></button>
          {retOpen && (
            <div className="p-5">
              <p className="text-sm text-muted-foreground mb-3">Flag living alumni records not updated in a set number of years, so staff can review and purge stale data. Deceased records are excluded.</p>
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Untouched for</label>
                  <div className="flex items-center gap-2">
                    <input data-testid="retention-years" type="number" min={1} max={50} value={retYears} onChange={(e) => setRetYears(e.target.value)} className="w-20 px-3 py-2 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
                    <span className="text-sm text-muted-foreground">years or more</span>
                  </div>
                </div>
                <button data-testid="retention-run" onClick={runRetentionReview} disabled={retBusy} className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{retBusy ? "Reviewing…" : "Run review"}</button>
              </div>
              {retData && (
                <div data-testid="retention-results">
                  <div className="flex items-center gap-2 text-sm mb-3">
                    <span className="text-2xl font-serif font-bold tabular-nums text-primary">{retData.count}</span>
                    <span className="text-muted-foreground">record{retData.count === 1 ? "" : "s"} not updated since {new Date(retData.cutoff).toLocaleDateString()}</span>
                  </div>
                  {retData.count > 0 && (
                    <div className="overflow-x-auto max-h-96 overflow-y-auto border border-border rounded-md">
                      <table className="w-full text-sm" data-testid="retention-table">
                        <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono sticky top-0"><tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Class</th><th className="text-left px-4 py-2">Last activity</th><th className="px-4 py-2"></th></tr></thead>
                        <tbody className="divide-y divide-border">
                          {retData.records.map((r) => (
                            <tr key={r.id} data-testid="retention-row" className="hover:bg-muted/30">
                              <td className="px-4 py-2">{r.name}{r.claimed && <span className="ml-1.5 text-[10px] text-emerald-700">✓ claimed</span>}</td>
                              <td className="px-4 py-2 text-muted-foreground">{r.class_year || "—"}</td>
                              <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{r.last_activity ? new Date(r.last_activity).toLocaleDateString() : "—"}</td>
                              <td className="px-4 py-2 text-right"><button data-testid="retention-open" onClick={() => navigate(`/alumni/${r.id}`)} className="text-xs text-primary hover:underline">Review →</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {emailModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60" data-testid="email-detail-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setEmailModal(null); }}>
          <div className="bg-card w-full max-w-4xl rounded-lg shadow-xl border border-border max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h2 className="font-serif text-xl tracking-tight">{emailModal.title}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{emailModal.rows.length} record{emailModal.rows.length === 1 ? "" : "s"} in this period</p>
              </div>
              <button data-testid="email-detail-close" onClick={() => setEmailModal(null)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-auto">
              {emailModal.rows.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-muted-foreground">No records in this period.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono sticky top-0"><tr>
                    <th className="text-left px-5 py-2">When</th>
                    <th className="text-left px-5 py-2">Alumnus</th>
                    <th className="text-left px-5 py-2">Detail</th>
                    <th className="text-left px-5 py-2">By</th>
                    <th className="text-left px-5 py-2">Current status</th>
                  </tr></thead>
                  <tbody className="divide-y divide-border" data-testid="email-detail-rows">
                    {emailModal.rows.map((r, i) => {
                      const st = r.current_status || "";
                      const stColor = st === "Subscribed" ? "bg-emerald-100 text-emerald-800" : st === "Opted out" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800";
                      return (
                      <tr key={i} className="hover:bg-muted/30">
                        <td className="px-5 py-2 whitespace-nowrap text-muted-foreground">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                        <td className="px-5 py-2">
                          <button onClick={() => { setEmailModal(null); navigate(`/alumni/${r.alumnus_id}`); }} className="text-primary hover:underline font-medium">
                            {r.alumnus}{r.class_year ? <span className="text-muted-foreground text-xs"> ’{String(r.class_year).slice(-2)}</span> : null}
                          </button>
                        </td>
                        <td className="px-5 py-2"><span className="text-[11px]">{r.action}</span>{r.detail ? <span className="text-muted-foreground text-[11px]"> — {r.detail}</span> : null}</td>
                        <td className="px-5 py-2 text-muted-foreground text-xs">{r.by_name || "—"}</td>
                        <td className="px-5 py-2">{st ? <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${stColor}`}>{st}</span> : "—"}</td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
